import React, { useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkBreaks from "remark-breaks";
import remarkMath from "remark-math";
import rehypeKatex from "rehype-katex";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faThumbsUp, faThumbsDown, faVolumeHigh, faVolumeXmark, faCopy, faCheck } from '@fortawesome/free-solid-svg-icons';

import "katex/dist/katex.min.css";

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

interface Props {
    type: "user" | "bot" | string;
    text: string;
}

// Reading markdown/LaTeX syntax aloud verbatim ("asterisk asterisk") is
// unreadable, so strip it down to plain words before sending it to TTS.
const stripForSpeech = (markdown: string) =>
    markdown
        .replace(/```[\s\S]*?```/g, " ")
        .replace(/`([^`]+)`/g, "$1")
        .replace(/\$\$[\s\S]*?\$\$/g, " ")
        .replace(/\$([^$]+)\$/g, "$1")
        .replace(/!\[([^\]]*)\]\([^)]*\)/g, "$1")
        .replace(/\[([^\]]*)\]\([^)]*\)/g, "$1")
        .replace(/[*_#>~`-]/g, " ")
        .replace(/\s+/g, " ")
        .trim();

export default function MessageBubble({ type, text }: Props) {
    const [copied, setCopied] = useState(false);
    const [isSpeaking, setIsSpeaking] = useState(false);
    const [isLoadingAudio, setIsLoadingAudio] = useState(false);
    const audioRef = useRef<HTMLAudioElement | null>(null);
    const audioUrlRef = useRef<string | null>(null);

    const copyMessage = async () => {
        await navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const releaseAudioUrl = () => {
        if (audioUrlRef.current) {
            URL.revokeObjectURL(audioUrlRef.current);
            audioUrlRef.current = null;
        }
    };

    const toggleSpeak = async () => {
        if (isSpeaking) {
            audioRef.current?.pause();
            setIsSpeaking(false);
            return;
        }

        if (isLoadingAudio) return;

        setIsLoadingAudio(true);

        try {
            const response = await fetch(`${API_BASE_URL}/api/tts`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ text: stripForSpeech(text) }),
            });

            if (!response.ok) {
                throw new Error(`TTS request failed with status ${response.status}`);
            }

            const blob = await response.blob();

            releaseAudioUrl();
            const url = URL.createObjectURL(blob);
            audioUrlRef.current = url;

            if (!audioRef.current) {
                audioRef.current = new Audio();
                audioRef.current.onended = () => setIsSpeaking(false);
                audioRef.current.onerror = () => setIsSpeaking(false);
            }

            audioRef.current.src = url;
            await audioRef.current.play();
            setIsSpeaking(true);
        } catch (error) {
            console.error("Error reading message aloud:", error);
        } finally {
            setIsLoadingAudio(false);
        }
    };

    useEffect(() => {
        return () => {
            audioRef.current?.pause();
            releaseAudioUrl();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div className={`message-row ${type === "user" ? "user-row" : "bot-row"}`}>
            <div
                className={`message-avatar ${type === "user" ? "user-avatar" : "bot-avatar"
                    }`}
            >
                {type === "user" ? "👤" : "🤖"}
            </div>

            <div className="message-content">
                {(type === "user" || text.length > 0) && (
                    <div
                        className={`message-card ${type === "user" ? "user-card" : "bot-card"
                            }`}
                    >
                        <ReactMarkdown
                            remarkPlugins={[
                                remarkGfm,
                                remarkBreaks,
                                remarkMath,
                            ]}
                            rehypePlugins={[rehypeKatex]}
                        >
                            {text}
                        </ReactMarkdown>
                    </div>
                )}

                <div className="message-footer">
                    {text.length > 0 && (
                        <button className="icon-btn" onClick={copyMessage}>
                            {copied ? (
                                <>
                                    <FontAwesomeIcon icon={faCheck} /> Copied
                                </>
                            ) : (
                                <>
                                    <FontAwesomeIcon icon={faCopy} />
                                </>
                            )}
                        </button>
                    )}
                    
                    {(type === "bot" && text.length > 0) && (
                        <>
                            <button
                                className={`icon-btn${isSpeaking ? " speaking" : ""}`}
                                title={isSpeaking ? "Stop reading" : "Read aloud"}
                                onClick={toggleSpeak}
                                disabled={isLoadingAudio}
                            >
                                {isLoadingAudio ? (
                                    <span className="spinner-border spinner-border-sm"></span>
                                ) : (
                                    <FontAwesomeIcon icon={isSpeaking ? faVolumeXmark : faVolumeHigh} />
                                )}
                            </button>
                            <button className="icon-btn"><FontAwesomeIcon icon={faThumbsUp} /></button>
                            <button className="icon-btn"><FontAwesomeIcon icon={faThumbsDown} /></button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}